import { isFunctionType } from '../../helpers/js-utils';
import DebugUANC from '../../core/version/debug';
import {
    getAdUnitNamesToRefresh,
    getAllSlotNames,
    getAvailableSlot,
    getServiceUtils,
    getSlotNamesByAdServer,
    groupExecution,
    IGroupSlotsByAdServer,
    mergePlacements,
    verifyAdParams,
} from './ad-services.utils';
import { IServices } from '../../uanc.types';
import UancEvent from '../UancEvent';
import {
    IArtSponsParams,
    IRefreshAdsInterval,
    ServiceSlotType,
    ServiceUtilsType,
    TargetingParamsType,
    TargetingType,
} from './ad-services.types';
import { AD_SERVERS, DEFAULT_ADS_REFRESH_INTERVAL, DEFAULT_MIN_ADS_REFRESH_INTERVAL } from '../../configs/constants';
import { serviceDFPUtils } from './DFP/service-dfp';
import { serviceADOCEANUtils } from './ADOCEAN/service-adocean';
import { isElemInView } from '../../helpers/elements-visibility';
import { IServiceADOCEANUtils } from './ADOCEAN/service-adocean.types';
import { IServiceDFPUtils } from './DFP/service-dfp.types';

const refreshAdsConfig: IRefreshAdsInterval = { ...DEFAULT_ADS_REFRESH_INTERVAL, enabled: false };
let refreshAdsTimerIdx: number;

/**
 * Globalnie dostępna metoda. Inicjuje osadzenie konkretnego slotu. W zależności od adserwera slotu wywołuje dedykowane funkcje dla adserwera.
 *
 * @param {string} slotName - nazwa slotu
 * @param {((slot?: ServiceSlotType) => void) | null} cb - opcjonalnie, callback do wywołania
 * @param {string | null} newPlacementId - opcjonalnie, nazwa id kontenera gdzie ma być osadzony slot
 * @param {TargetingType | null} targeting - opcjonalnie, ustawienie targetowania dla slotu
 * @returns {void}
 */
export const insertAd = async (
    slotName: string,
    cb: ((slot?: ServiceSlotType) => void) | null = null,
    newPlacementId: string | null = null,
    targeting: TargetingType | null = null
): Promise<void> => {
    const { Ad } = window.Inpl;
    const services = Ad.services as IServices;
    const event = Ad.event as UancEvent;
    try {
        verifyAdParams(slotName, targeting);
        const slotConfig: ServiceSlotType | null = getAvailableSlot(slotName, newPlacementId ? [newPlacementId] : null);
        if (!slotConfig) return;

        if (services.TesterService?._tests && slotConfig.isTest) {
            // osadzenie / aktualizacja testowego slotu
            const tester = await import('../plugins/Tester/Tester');
            tester.updateTestedSlot(slotName, newPlacementId ? [newPlacementId] : null);
            return;
        }

        //newPlacementId - aktualizacja listy kontenerów
        newPlacementId && (slotConfig.placementIds = mergePlacements(slotConfig.name, slotConfig.placementIds, [newPlacementId]));
        const placementIdToInsert = newPlacementId ? [newPlacementId] : slotConfig.placementIds;

        const service: ServiceUtilsType = getServiceUtils(slotConfig.adServer);
        slotConfig.isLazyLoading
            ? service.lazyLoadInsertAd(slotConfig, placementIdToInsert, targeting)
            : groupExecution(slotConfig, placementIdToInsert, targeting);

        if (cb && isFunctionType(cb)) {
            event.add(`Inpl.${slotName}`, window, () => cb(slotConfig));
        }
    } catch (error) {
        Ad.UANC instanceof DebugUANC &&
            Ad.UANC.warn(`[insertAd] Problem with insert slot: ${slotName}. ${error instanceof Error ? error.message : error}`);
    }
};

/**
 * Globalnie dostępna metoda. Inicjuje osadzenie slotów z opóźnieniem - lazy loading. W zależności od adserwera slotu wywołuje dedykowane funkcje dla adserwera.
 *
 * @param {string} slotName - nazwa slotu do osadzenia
 * @param {string[] | null} newPlacementIds - opcjonalnie, nazwy id kontenerów, w których slot ma zostać osadzony
 * @param {TargetingType | null} targeting - opcjonalnie, ustawienie targetowania dla slotu
 *
 * @returns {void}
 */
const lazyLoadInsertAd = async (
    slotName: string,
    newPlacementIds: string[] | null,
    targeting: TargetingType | null = null
): Promise<void> => {
    const { Ad } = window.Inpl;
    const services = Ad.services as IServices;

    try {
        verifyAdParams(slotName, targeting);
        const slotConfig: ServiceSlotType | null = getAvailableSlot(slotName, newPlacementIds);
        if (!slotConfig) return;

        if (services.TesterService?._tests && slotConfig.isTest) {
            // osadzenie / aktualizacja testowego slotu
            const tester = await import('../plugins/Tester/Tester');
            tester.updateTestedSlot(slotName, newPlacementIds?.length ? newPlacementIds : null);
            return;
        }

        //newPlacementIds - aktualizacja listy kontenerów o wskazane placementy
        newPlacementIds?.length && (slotConfig.placementIds = mergePlacements(slotConfig.name, slotConfig.placementIds, newPlacementIds));
        const placementIdsToInsert = newPlacementIds?.length ? newPlacementIds : slotConfig.placementIds;

        const service: ServiceUtilsType = getServiceUtils(slotConfig.adServer);
        slotConfig.isLazyLoading
            ? service.lazyLoadInsertAd(slotConfig, placementIdsToInsert, targeting)
            : groupExecution(slotConfig, placementIdsToInsert, targeting);
    } catch (error) {
        Ad.UANC instanceof DebugUANC &&
            Ad.UANC.warn(`[lazyLoadInsertAd] Problem with slot: ${slotName}. ${error instanceof Error ? error.message : error}`);
    }
};

/**
 * Globalnie dostępna metoda.
 * Inicjuje odświeżenie konkretnego slotu, o ile znajduje się aktualnie w viewport.
 * Odświeżenie slotu AO odświeży również pozostałe reklamy adserwera AO.
 * W zależności od adserwera slotu wywołuje dedykowane funkcje dla adserwera.
 *
 * @param {string} slotName - nazwa slotu
 * @param {string | null} placementIdToRefresh - opcjonalnie, nazwa id kontenera, w którym ma zostać odświeżona reklama
 * @param {TargetingType | null} targeting - opcjonalnie, ustawienie targetowania dla slotu
 * @returns {void}
 */
export const refreshAd = (slotName: string, placementIdToRefresh: string | null = null, targeting: TargetingType | null = null): void => {
    const { Ad } = window.Inpl;
    try {
        verifyAdParams(slotName, targeting);
        const slotConfig: ServiceSlotType | null = getAvailableSlot(slotName);
        if (!slotConfig || slotConfig.isTest) return;

        //jeżeli wskazane zostało inne miejsce osadzenia niż aktualnie istniejące to nie jest obsługiwany refresh
        if (placementIdToRefresh && !slotConfig.placementIds.includes(placementIdToRefresh)) return;

        // sprawdzenie wskazanego miejsca do odświeżenia lub sprawdzenie wszystkich placementów slotu
        const placementsToCheck = placementIdToRefresh ? [placementIdToRefresh] : slotConfig.placementIds;
        // nazwy placementów znajdujących się w viewport
        const placementNamesInView: string[] = placementsToCheck.filter((placementId: string) => {
            const placementDOM: HTMLElement | null = document.getElementById(placementId);
            if (!placementDOM) return false;
            return isElemInView(placementDOM);
        });

        if (!placementNamesInView.length) throw new Error('There are no placement in viewport for the specified slot.');

        const service: ServiceUtilsType = getServiceUtils(slotConfig.adServer);
        slotConfig.adServer === AD_SERVERS.ADOCEAN
            ? (service as IServiceADOCEANUtils).refreshAds()
            : (service as IServiceDFPUtils).refreshAd(slotConfig, placementNamesInView, targeting);
    } catch (error) {
        Ad.UANC instanceof DebugUANC &&
            Ad.UANC.warn(`[refreshAd] Problem with refresh slot: ${slotName}. ${error instanceof Error ? error.message : error}`);
    }
};

/**
 * Globalnie dostępna metoda.
 * Jeżeli nie zostaną określone konkretne sloty, odświeżone zostaną wszystkie na ekranie. Sloty testowe są pomijane.
 * Inicjuje odświeżenie slotów będących aktualnie w widoku użytkownika dla DFP.
 * Jeżeli w viewport znajduje się pojedynczy slot AO, inicjuje on odświeżenie pozostałych reklam adserwera AO (brak możliwości odświeżenia pojedynczego slotu).
 * W zależności od adserwera slotu wywołuje dedykowane funkcje dla adserwera.
 *
 * @param {string[] | null} slotsNames - opcjonalnie, nazwy slotów do odświeżenia
 * @param {TargetingType | null} targeting - opcjonalnie, ustawienie targetowania dla reklam
 * @returns {void}
 */
export const refreshAds = (slotsNames: string[] | null = null, targeting: TargetingType | null = null): void => {
    const { Ad } = window.Inpl;
    const event = Ad.event as UancEvent;
    try {
        // nazwy slotów potencjalnie do odświeżenia
        const slotNamesToRefresh: string[] = slotsNames?.length ? slotsNames : getAllSlotNames();
        const allAdUnitNamesToRefresh: string[] = [];

        // podział slotów na adserwery
        const collectedSlots: IGroupSlotsByAdServer = getSlotNamesByAdServer(slotNamesToRefresh);
        Object.keys(collectedSlots).forEach((adServerName) => {
            const adServer = adServerName as AD_SERVERS;
            if (collectedSlots[adServer]?.length) {
                const slotNamesPerServer = collectedSlots[adServer] as string[];
                // nazwy placementów możliwych do odświeżenia - znajdujących się w viewport
                const adUnitNamesToRefresh = getAdUnitNamesToRefresh(slotNamesPerServer, adServer, !!slotsNames);
                allAdUnitNamesToRefresh.push(...adUnitNamesToRefresh);

                if (adUnitNamesToRefresh.length) {
                    const service = getServiceUtils(adServer);
                    adServer === AD_SERVERS.ADOCEAN
                        ? (service as IServiceADOCEANUtils).refreshAds()
                        : service.refreshAds(adUnitNamesToRefresh, targeting);
                }
            }
        });

        Ad.UANC instanceof DebugUANC &&
            Ad.UANC.warn(`[refreshAds] adsToRefresh: ${allAdUnitNamesToRefresh.length ? allAdUnitNamesToRefresh : '---'}.`);
        event?.custom('Inpl.Ad.refreshAds', { type: 'custom', ads: allAdUnitNamesToRefresh });
    } catch (error) {
        Ad.UANC instanceof DebugUANC &&
            Ad.UANC.warn(
                `[refreshAds] Problem with refresh ${slotsNames ? 'specific slots: ' + slotsNames.join(', ') : 'all slots'}. ${error instanceof Error ? error.message : error}`
            );
    }
};

/**
 * Globalnie dostępna metoda. Inicjuje usunięcie slotu. W zależności od adserwera slotu wywołuje dedykowane funkcje dla adserwera.
 *
 * @param {string} slotName - nazwa slotu
 * @param {string | null} placementId - opcjonalnie, nazwa id kontenera, który ma zostać usunięty
 * @returns {void}
 */
const destroyAd = (slotName: string, placementId: string | null = null): void => {
    const { Ad } = window.Inpl;
    try {
        verifyAdParams(slotName);
        const slotConfig: ServiceSlotType | null = getAvailableSlot(slotName);
        if (!slotConfig) return;

        const service: ServiceUtilsType = getServiceUtils(slotConfig.adServer);
        service.destroyAd(slotConfig, placementId);
    } catch (error) {
        Ad.UANC instanceof DebugUANC &&
            Ad.UANC.warn(`[destroyAd] Problem with destroy slot: ${slotName}. ${error instanceof Error ? error.message : error}`);
    }
};

/**
 * Globalnie dostępna metoda. Inicjuje usunięcie wskazanych slotów.
 *
 * @param {string} slotNames - nazwy slotów
 * @returns {void}
 */
const destroyAds = (slotNames: string[]): void => {
    const { Ad } = window.Inpl;
    try {
        slotNames.forEach((slotName) => {
            verifyAdParams(slotName);
            const slotConfig: ServiceSlotType | null = getAvailableSlot(slotName);
            if (!slotConfig) return;
            if (!slotConfig.renderedPlacements) {
                throw new Error(`${slotName} doesn't have rendered adUnits.`);
            }

            const service: ServiceUtilsType = getServiceUtils(slotConfig.adServer);
            Object.keys(slotConfig.renderedPlacements).forEach((placementId) => {
                service.destroyAd(slotConfig, placementId);
            });
        });
    } catch (error) {
        Ad.UANC instanceof DebugUANC &&
            Ad.UANC.warn(`[destroyAds] Problem with destroy slots. ${error instanceof Error ? error.message : error}`);
    }
};

/**
 * Globalnie dostępna metoda. Obsługująca wyłącznie AdOcean. Inicjuje metodę odpowiedzialną za zliczajanie pustych miejsc reklamowych.
 *
 * @param {string[]} hbhtArgs - parametry
 * @returns {void}
 */
const hbht = (...hbhtArgs: string[]): void => {
    const services = window.Inpl.Ad.services as IServices;
    if (services.ADOCEANService) {
        serviceADOCEANUtils.hbht(...(hbhtArgs as [string, string, string, string, string]));
    }
};

/**
 * Globalnie dostępna metoda. Inicjuje zapis informacji odnośnie artukułu sponsorowanego. Wywołuje dedykowane funkcje dla konkretnych adserwerów.
 *
 * @param {string[]} name - nazwa klucza w cache
 * @param {IArtSponsParams | null} data - parametry do zapisania - dane na podstawie, których budowany jest bloczek reklamy
 * @param {boolean} isDFP - opcjonalnie, defaultowo ustawione false - adserver AO, informacja, dla którego adserwera wywoałać metodę
 * @returns {void}
 */
const setArtSponsParams = (name: string, data: IArtSponsParams | null, isDFP: boolean = false): void => {
    const services = window.Inpl.Ad.services as IServices;
    const service = isDFP ? services.DFPService : services.ADOCEANService;

    if (service) {
        const service: ServiceUtilsType = getServiceUtils(isDFP ? AD_SERVERS.DFP : AD_SERVERS.ADOCEAN);
        service.setArtSponsParams(name, data);
    }
};

/**
 * Globalnie dostępna metoda. Inicjuje odczyt informacji odnośnie artukułu sponsorowanego (używane m.in. w Inpl.Sponsored). Wywołuje dedykowane funkcje dla konkretnych adserwerów.
 *
 * @param {string[]} name - nazwa klucza
 * @param {(data:IArtSponsParams) => void} cb - opcjonalnie, funkcja wywołana po odczycie informacji, przekazuje pobrane parametry reklamy
 * @param {boolean} isDFP - opcjonalnie, defaultowo ustawione false - adserver AO, informacja, dla którego adserwera wywoałać metodę
 * @returns {void}
 */
const getArtSponsParams = (
    name: string,
    cb: ((data: IArtSponsParams | null) => void) | null = null,
    isDFP: boolean = false
): IArtSponsParams | null => {
    const services = window.Inpl.Ad.services as IServices;
    const service = isDFP ? services.DFPService : services.ADOCEANService;

    if (service && name) {
        const service: ServiceUtilsType = getServiceUtils(isDFP ? AD_SERVERS.DFP : AD_SERVERS.ADOCEAN);
        return service.getArtSponsParams(name, cb);
    } else return null;
};

/**
 * Globalnie dostępna metoda. Wywołuje przekazany callback w momencie pełnego załadowania.
 *
 * @param {() =>void} cb - callback wywołany na evencie onReady
 * @returns {void}
 */
const onReady = (cb: () => void): void => {
    try {
        const event = window.Inpl.Ad.event;
        if (event) {
            event.onReady(cb);
        }
    } catch (e) {
        console.log(e);
    }
};

/**
 * Globalnie dostępna metoda ustawiająca targetowanie dla serwera GAM. Obsługująca wyłącznie adserwer DFP.
 *
 * @param {string} key - klucz targetowania
 * @param {string | string[]} params - wartości parametru targetowania
 * @returns {void}
 */
const setTargeting = (key: string, params: TargetingParamsType) => {
    const services = window.Inpl.Ad.services as IServices;
    if (services.DFPService) {
        serviceDFPUtils.setTargeting(key, params);
    }
};

/**
 * Globalnie dostępna metoda. Przekazuje ustawione wartości targetowania serwera GAM, dla konkretnego parametru. Obsługująca wyłącznie adserwer DFP.
 *
 * @param {string} key - nazwa parametru
 * @returns {string[] | null} parametry targetowania
 */
const getTargeting = (key: string): string[] | null => {
    const { Ad } = window.Inpl;
    return Ad.services?.DFPService ? serviceDFPUtils.getTargeting(key) : null;
};

/**
 * Globalnie dostępna metoda. Zmienia domyślną konfigurację refresh.
 *
 * @param {boolean} enabled - flaga czy refresh włączony/wyłączony
 * @param {number} interval - czas odświeżania w sekundach
 * @returns {IRefreshAdsInterval} konfiguracja refresh
 */
const setRefreshAdsInterval = (enabled: boolean, interval: number): IRefreshAdsInterval => {
    if (typeof enabled === 'boolean' && typeof interval === 'number') {
        const { Ad } = window.Inpl;

        refreshAdsConfig.enabled = enabled;
        refreshAdsConfig.interval = interval < DEFAULT_MIN_ADS_REFRESH_INTERVAL ? DEFAULT_MIN_ADS_REFRESH_INTERVAL : interval;
        Ad.UANC instanceof DebugUANC &&
            Ad.UANC.log('Set Refresh Ads Interval', refreshAdsConfig.enabled, refreshAdsConfig.interval + ' sek.');
    }
    return refreshAdsConfig;
};

const handleVisibilityChange = () => {
    if (document.hidden) {
        clearInterval(refreshAdsTimerIdx);
    } else {
        refreshAdsTimerIdx = window.setInterval(() => {
            refreshAdsConfig.enabled && refreshAds();
        }, refreshAdsConfig.interval * 1000);
    }
};

/**
 * Globalnie dostępna metoda. Inicjuje odświeżanie reklam.
 *
 * @returns {void}
 */
const initAdsRefresh = (): void => {
    if (!refreshAdsConfig.enabled) return;

    const { Ad } = window.Inpl;
    clearInterval(refreshAdsTimerIdx);
    document.addEventListener('visibilitychange', handleVisibilityChange, false);
    handleVisibilityChange();

    Ad.UANC instanceof DebugUANC && Ad.UANC.log('Init Ads Refresh', new Date().toLocaleTimeString());
};

/**
 * Funkcja odpowiedzialna za przypisane do obiektu globalnego window.Inpl.Ad ogólnodostępnych metod obsługujących reklamy.
 *
 * @returns {void}
 */
export const setGlobalAdServices = (): void => {
    const { Ad } = window.Inpl;

    window.Inpl.Ad = {
        ...Ad,
        insertAd,
        lazyLoadInsertAd,
        refreshAd,
        refreshAds,
        destroyAd,
        destroyAds,
        hbht,
        set: setArtSponsParams,
        get: getArtSponsParams,
        onReady,
        setTargeting,
        getTargeting,
        setRefreshAdsInterval,
        initAdsRefresh,
        clearAds: [],
    };
};
